import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@repo/ui/components/ui/dialog";
import { ProfilSelectOption } from "@repo/ui/lib/api/types/profil";
import { motion } from "framer-motion";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { useProfils } from "../../hooks/api/profil";
import { useUpdateUserProfile } from "../../hooks/api/user";

interface ChangeProfileDialogProps {
  userId: number;
  currentProfileId?: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ChangeProfileDialog({
  userId,
  currentProfileId,
  isOpen,
  onClose,
  onSuccess
}: ChangeProfileDialogProps) {
  const { data: profileOptions, isLoading: isLoadingProfiles } = useProfils();
  const updateUserProfile = useUpdateUserProfile();
  const [selectedProfileId, setSelectedProfileId] = useState<number | undefined>(currentProfileId);

  const handleSubmit = async () => {
    if (!selectedProfileId) return;

    try {
      await updateUserProfile.mutateAsync({ profilId: selectedProfileId, userId });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating user profile:", error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Changer le profil de l'utilisateur</DialogTitle>
          <DialogDescription>
            Sélectionnez un nouveau profil pour cet utilisateur. Cela modifiera les permissions et les accès.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="grid w-full items-center gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="profile">Profil</Label>
              {isLoadingProfiles ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Select
                  value={selectedProfileId?.toString() || ""}
                  onValueChange={(value) => setSelectedProfileId(Number(value))}
                >
                  <SelectTrigger id="profile">
                    <SelectValue placeholder="Sélectionner un profil" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {profileOptions?.data?.map((profile: ProfilSelectOption) => (
                      <SelectItem key={profile.id} value={profile.id.toString()}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <motion.div
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            <Button
              onClick={handleSubmit}
              disabled={!selectedProfileId || updateUserProfile.isPending || selectedProfileId === currentProfileId}
            >
              {updateUserProfile.isPending ? "Mise à jour..." : "Enregistrer"}
            </Button>
          </motion.div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
